import type { Route } from "./+types/login";
import { useLoaderData } from "react-router";
import { StytchB2B } from "@stytch/react/b2b";
import { StytchEventType } from "@stytch/vanilla-js";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useEnvironment } from "~/contexts/EnvironmentContext";
import { get_stytch_config, stytch_styles } from "~/lib/stytch-config";
import { get_site_logo } from "~/lib/.server/sanity/auth_sidebar";

export function meta({}: Route.MetaArgs) {
  return [
    { title: "Sign In | Orchestraight" },
    { name: "description", content: "Sign in to your Orchestraight account" },
  ];
}

export async function loader(_args: Route.LoaderArgs) {
  const logo_url = await get_site_logo();

  return { logo_url };
}

// Client-only Stytch wrapper to prevent SSR issues
function StytchLogin() {
  const [is_mounted, set_is_mounted] = useState(false);
  const environment = useEnvironment();
  const has_redirected = useRef(false);

  useEffect(() => {
    set_is_mounted(true);
  }, []);

  // Memoize config to avoid recreating on every render
  // Uses environment context for SSR-safe URL generation
  const stytch_config = useMemo(() => {
    if (!is_mounted) return null;
    return get_stytch_config(environment);
  }, [is_mounted, environment]);

  // After password login + org pick, Stytch's prebuilt UI lands on a
  // confirmation screen and stops. Hard-navigate to /dashboard ourselves so a
  // multi-org password login doesn't dead-end on /login. Window.location is
  // used (not react-router navigate) because Stytch dispatches more state
  // updates after onEvent — client-side nav unmounts mid-render.
  const handle_event = useCallback((event: { type: string }) => {
    if (has_redirected.current) return;
    if (
      event.type === StytchEventType.B2BDiscoveryIntermediateSessionExchange ||
      event.type === StytchEventType.B2BPasswordAuthenticate ||
      event.type === StytchEventType.AuthenticateFlowComplete
    ) {
      has_redirected.current = true;
      window.location.href = "/dashboard";
    }
  }, []);

  if (!is_mounted || !stytch_config) {
    return (
      <div className="text-center">
        <span className="loading loading-spinner loading-lg text-primary"></span>
        <p className="mt-4">Loading...</p>
      </div>
    );
  }

  return (
    <StytchB2B
      config={stytch_config}
      styles={stytch_styles}
      callbacks={{ onEvent: handle_event }}
    />
  );
}

export default function LoginPage() {
  const { logo_url } = useLoaderData<typeof loader>();

  return (
    <div className="flex min-h-[calc(100vh-10.6rem)] flex-col items-center justify-center bg-base-200 p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          {logo_url ? (
            <img
              src={logo_url}
              alt="Orchestraight"
              className="mx-auto mb-4 h-10 w-auto"
            />
          ) : (
            <h1 className="text-3xl font-bold">Orchestraight</h1>
          )}
          <p className="mt-2 text-base-content/70">
            Sign in or create an account
          </p>
        </div>
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <StytchLogin />
          </div>
        </div>
      </div>
    </div>
  );
}
